import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { normalizeProductForCart } from "../../utils/normalizeCartProduct";

export interface CartItem {
  id?: string;
  _id?: string;
  productId?: string;

  userId?: string;
  sellerUserId?: string;
  ownerId?: string;
  sellerId?: string;
  sellerBusinessName?: string;
  dispatchWarehouseId?: string;

  title: string;
  image?: string;
  imageUrl?: string;
  selectedImage?: string;
  resolvedImage?: string;
  thumbnail?: string;
  images?: string[];

  description?: string;
  category?: string;

  price: number;
  selectedPrice?: number;
  quantity: number;

  selectedVariants?: any[];
  variantSummary?: string;

  isPhysical?: boolean;
  weightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  bulky?: boolean;

  [key: string]: any;
}

export interface CartState {
  items: CartItem[];
  total: number;
}

const initialState: CartState = {
  items: [],
  total: 0,
};

const getCartItemId = (item: Partial<CartItem>) =>
  String(item._id || item.id || item.productId || "").trim();

const getCartItemVariantKey = (item: Partial<CartItem>) => {
  const selectedVariants = Array.isArray(item.selectedVariants)
    ? item.selectedVariants
    : [];

  if (selectedVariants.length === 0) return "";

  return JSON.stringify(
    selectedVariants.map((variant: any) => ({
      attributeName: variant.attributeName || variant.name || "",
      value: variant.value || variant.label || "",
    })),
  );
};

const recalculateTotal = (state: CartState) => {
  state.total = state.items.reduce((sum, item) => {
    const price = Number(item.selectedPrice ?? item.price ?? 0);
    const quantity = Number(item.quantity || 0);
    return sum + price * quantity;
  }, 0);
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<any>) => {
      const item = normalizeProductForCart(action.payload);

      const itemId = getCartItemId(item);
      const itemVariantKey = getCartItemVariantKey(item);

      const existing = state.items.find((cartItem) => {
        const cartItemId = getCartItemId(cartItem);
        const cartItemVariantKey = getCartItemVariantKey(cartItem);

        return cartItemId === itemId && cartItemVariantKey === itemVariantKey;
      });

      if (existing) {
        existing.quantity =
          Number(existing.quantity || 0) + Number(item.quantity || 1);

        /**
         * Keep seller/warehouse fields fresh in case older localStorage items
         * did not have them before this marketplace checkout update.
         */
        existing.userId = item.userId;
        existing.sellerUserId = item.sellerUserId;
        existing.ownerId = item.ownerId;
        existing.sellerBusinessName = item.sellerBusinessName;
        existing.dispatchWarehouseId = item.dispatchWarehouseId;

        existing.image = item.image;
        existing.imageUrl = item.imageUrl;
        existing.selectedImage = item.selectedImage;
        existing.selectedPrice = item.selectedPrice;
      } else {
        state.items.push(item);
      }

      recalculateTotal(state);
    },

    removeFromCart: (state, action: PayloadAction<string>) => {
      const id = String(action.payload || "").trim();

      state.items = state.items.filter((item) => {
        const itemId = getCartItemId(item);
        return itemId !== id;
      });

      recalculateTotal(state);
    },

    increaseQuantity: (state, action: PayloadAction<string>) => {
      const id = String(action.payload || "").trim();

      const item = state.items.find(
        (cartItem) => getCartItemId(cartItem) === id,
      );

      if (item) {
        item.quantity = Number(item.quantity || 0) + 1;
      }

      recalculateTotal(state);
    },

    decreaseQuantity: (state, action: PayloadAction<string>) => {
      const id = String(action.payload || "").trim();

      const item = state.items.find(
        (cartItem) => getCartItemId(cartItem) === id,
      );

      if (item) {
        item.quantity = Math.max(1, Number(item.quantity || 1) - 1);
      }

      recalculateTotal(state);
    },

    clearCart: (state) => {
      state.items = [];
      state.total = 0;
    },

    setCart: (state, action: PayloadAction<CartState>) => {
      state.items = Array.isArray(action.payload.items)
        ? action.payload.items.map((item) => normalizeProductForCart(item))
        : [];

      state.total =
        typeof action.payload.total === "number" ? action.payload.total : 0;

      recalculateTotal(state);
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  increaseQuantity,
  decreaseQuantity,
  clearCart,
  setCart,
} = cartSlice.actions;

export default cartSlice.reducer;
